/**
 * Pin to dashboard (S20) — provenance capture from host surfaces (QE results
 * grid, QE chart view, Table Explorer, execution-plan viewer, Session
 * Browser). A surface calls `requestPinToDashboard` with what it knows; the
 * globally-mounted PinConfirmSheet resolves the rest (dashboard pick, title,
 * autopilot suggestion) and lands the tile through the S1 store → repository.
 * Pure builders live here; the sheet owns all IO.
 */
import { create } from 'zustand';
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { DashboardEngineId, DashboardTile, NoteTile } from '../types/dashboardTypes';
import { shapeSuggest, type ResultShape, type TileSuggestion } from '../utils/shapeSuggest';
import { DEFAULT_TILE_SIZE } from '../utils/tileDefaultSize';
import { buildTileFromSuggestion } from './aiProposalToTile';

export type PinSource = 'qe-results' | 'qe-chart' | 'table-explorer' | 'plan' | 'session-browser';

export type PinRequest = {
  from: PinSource;
  /** The exact SQL the surface executed (or would execute) — lands verbatim. */
  sql: string;
  connectionId: string;
  database?: string;
  /** Prefills the sheet's title input. */
  titleHint?: string;
  /** Chart pins keep their live configuration — the tile lands as a chart. */
  chartConfig?: ChartConfig;
  /** Result shape when the surface has one — drives the shape autopilot. */
  shape?: ResultShape;
};

type PinToDashboardState = {
  request: PinRequest | null;
  /** Bumped per open — keys the sheet so repeat pins reset its local state. */
  nonce: number;
  open: (request: PinRequest) => void;
  close: () => void;
};

export const usePinToDashboardStore = create<PinToDashboardState>((set) => ({
  request: null,
  nonce: 0,
  open: (request) => set((s) => ({ request, nonce: s.nonce + 1 })),
  close: () => set({ request: null }),
}));

/** Surface entry point — callable outside React (menus, toolbars). */
export function requestPinToDashboard(request: PinRequest): void {
  usePinToDashboardStore.getState().open(request);
}

/** Last-resort per-surface names when the SQL yields no relation at all. */
const SOURCE_TITLE: Record<PinSource, string> = {
  'qe-results': 'Query results',
  'qe-chart': 'Chart',
  'table-explorer': 'Table rows',
  plan: 'Plan query',
  'session-browser': 'Session query',
};

const unquoteIdentifier = (part: string): string => part.replace(/^[["`']|[\]"`']$/g, '');

const IDENT = String.raw`(?:[\w$#@]+|"[^"]+"|\[[^\]]+\]|\`[^\`]+\`)`;
const RELATION_RE = new RegExp(String.raw`\b(?:from|join)\s+(${IDENT}(?:\s*\.\s*${IDENT}){0,2})`, 'i');

/**
 * First relation a query reads from — the closest thing to a human name a
 * pinned query carries. Deliberately shallow (no parser): it only ever feeds
 * a DEFAULT title the user can overwrite, never SQL. Sub-selects and CTE
 * references simply resolve to whatever identifier follows the first
 * FROM/JOIN; a query with none falls back to the surface name.
 */
export function primaryRelationName(sql: string): string | undefined {
  const match = RELATION_RE.exec(sql);
  if (!match) return undefined;
  const parts = match[1]
    .split('.')
    .map((p) => unquoteIdentifier(p.trim()))
    .filter((p) => p.length > 0);
  if (parts.length === 0) return undefined;
  // Drop a database qualifier — `db.schema.table` reads as `schema.table`.
  return parts.slice(-2).join('.');
}

/**
 * The pin sheet's prefilled title (S24 review): the generic "Pinned query"
 * told the user nothing about what they pinned. Order: the host surface's
 * explicit hint (Table Explorer's `schema.table`, Session Browser's session
 * label) → the pinned chart's own title → the query's primary relation → a
 * per-surface fallback.
 */
export function defaultPinTitle(request: PinRequest): string {
  const hint = request.titleHint?.trim();
  if (hint) return hint;
  const chartTitle = request.chartConfig?.title?.trim();
  if (chartTitle) return chartTitle;
  const relation = primaryRelationName(request.sql);
  if (!relation) return SOURCE_TITLE[request.from];
  return request.from === 'qe-chart' ? `${relation} chart` : relation;
}

/** What the sheet shows as "Detected: …" and what the tile builds from. */
export function suggestForPin(request: PinRequest): TileSuggestion {
  if (request.chartConfig) {
    return {
      tileType: 'chart',
      chartConfig: request.chartConfig,
      reason: 'Keeps the chart configuration you pinned',
    };
  }
  if (request.shape && request.shape.columns.length > 0) {
    return shapeSuggest(request.shape).best;
  }
  return { tileType: 'table', reason: 'No result shape captured — lands as a table' };
}

const nextFreeY = (tiles: DashboardTile[]): number =>
  tiles.reduce((max, t) => Math.max(max, t.layout.y + t.layout.h), 0);

export function buildPinnedTile(args: {
  request: PinRequest;
  title: string;
  suggestion: TileSuggestion;
  engine?: DashboardEngineId;
  existingTiles: DashboardTile[];
}): Exclude<DashboardTile, NoteTile> {
  const { request, title, suggestion, engine, existingTiles } = args;
  return buildTileFromSuggestion({
    // proposal.tileType must fit the AI union (no 'map' — only title + sql
    // are consumed by the builder; the tile's real type is the suggestion's).
    proposal: {
      id: '',
      title,
      tileType: suggestion.tileType === 'map' ? 'table' : suggestion.tileType,
      sql: request.sql,
    },
    suggestion,
    connectionId: request.connectionId,
    database: request.database,
    engine,
    layout: { x: 0, y: nextFreeY(existingTiles), ...DEFAULT_TILE_SIZE[suggestion.tileType] },
  });
}
